import { useState, type FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { categories } from "@/mocks/stories";

const FORM_URL = "https://readdy.ai/api/form/daij763p14a1h3mr7550";
const FORM_ID = "anonymous-tip-form";
const CONTENT_LIMIT = 500;

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "我要爆料 - 匿名投稿入口｜黑暗爆料",
    url: "https://heianbaoliao.example.com/submit",
    description: "通过匿名入口向黑暗爆料投递线索，不收集可识别身份信息。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://heianbaoliao.example.com/" },
      { "@type": "ListItem", position: 2, name: "我要爆料", item: "https://heianbaoliao.example.com/submit" },
    ],
  },
];

export default function SubmitPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [contentLength, setContentLength] = useState(0);

  useSeo({
    title: "我要爆料 - 匿名投稿入口｜黑暗爆料",
    description:
      "向黑暗爆料匿名投递线索：填写事件概况、涉及领域与你掌握的材料类型。投稿不收集可识别身份信息，编辑部会在三到七个工作日内完成初筛。",
    keywords: "匿名投稿,我要爆料,线索投递,黑暗爆料投稿,知情人爆料",
    path: "/submit",
    jsonLd,
  });

  useScrollReveal([]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    setFormError("");

    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      formData.delete("website_alt");
      setStatus("success");
      formEl.reset();
      setContentLength(0);
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
        setContentLength(0);
      } else {
        setStatus("error");
        setFormError(
          serverMsg && serverMsg !== "OK" ? String(serverMsg) : "提交失败，请稍后重试，或通过公开邮箱联系我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交。请检查网络后重试，你的输入已保留。");
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "我要爆料" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
            我要爆料
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            你知道的那一部分，可能正是别人缺的那一块。不需要准备完整的证据，把你看到的、听到的、经历过的说清楚就好，剩下的交给编辑部去核实与整理。
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-content grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="reveal rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-checkbox-circle-line text-4xl" aria-hidden="true"></i>
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">已收到你的线索</h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
                  感谢你的信任。编辑部通常会在三到七个工作日内完成初筛，如果线索具备核查价值，我们会通过你留下的安全联系方式与你跟进。你没有填写联系方式也没关系。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-add-line" aria-hidden="true"></i>
                  再提交一条线索
                </button>
              </div>
            ) : (
              <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="tip-title" className="block text-sm font-label font-medium text-foreground-800">
                      线索标题 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="tip-title"
                      name="title"
                      type="text"
                      required
                      maxLength={80}
                      placeholder="用一句话概括这条线索"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                    />
                  </div>
                  <div>
                    <label htmlFor="tip-category" className="block text-sm font-label font-medium text-foreground-800">
                      所属栏目 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="tip-category"
                      name="category"
                      required
                      defaultValue=""
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                    >
                      <option value="" disabled>
                        请选择最接近的栏目
                      </option>
                      {categories
                        .filter((c) => c.id !== "all")
                        .map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="tip-region" className="block text-sm font-label font-medium text-foreground-800">
                      涉及地区 / 场景
                    </label>
                    <input
                      id="tip-region"
                      name="region"
                      type="text"
                      maxLength={40}
                      placeholder="如：华东某市、某连锁门店（可不填具体名称）"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                    />
                  </div>
                  <div>
                    <label htmlFor="tip-materials" className="block text-sm font-label font-medium text-foreground-800">
                      你掌握的材料类型
                    </label>
                    <select
                      id="tip-materials"
                      name="materials"
                      defaultValue=""
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                    >
                      <option value="">暂不选择</option>
                      <option value="仅有口述与见闻">仅有口述与见闻</option>
                      <option value="有文字记录或聊天截图">有文字记录或聊天截图</option>
                      <option value="有单据、台账或合同">有单据、台账或合同</option>
                      <option value="有录音或视频线索">有录音或视频线索</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="tip-content" className="block text-sm font-label font-medium text-foreground-800">
                      线索内容 <span className="text-primary-600">*</span>
                    </label>
                    <span
                      className={`text-xs font-label ${
                        contentLength > CONTENT_LIMIT ? "text-primary-600" : "text-foreground-500"
                      }`}
                    >
                      {contentLength} / {CONTENT_LIMIT}
                    </span>
                  </div>
                  <textarea
                    id="tip-content"
                    name="content"
                    required
                    rows={7}
                    maxLength={CONTENT_LIMIT}
                    onChange={(e) => setContentLength(e.target.value.length)}
                    placeholder="请尽量描述清楚：事情是什么时候发生的、涉及哪些环节、有哪些人可以印证。不需要写出真实姓名，用代号或描述也可以。"
                    className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-100 px-3.5 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  ></textarea>
                </div>

                <div>
                  <label htmlFor="tip-email" className="block text-sm font-label font-medium text-foreground-800">
                    安全联系方式（可选）
                  </label>
                  <input
                    id="tip-email"
                    name="email"
                    type="email"
                    maxLength={60}
                    placeholder="一个你可以安全接收邮件的地址，用于编辑部跟进"
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  />
                  <p className="mt-2 text-xs text-foreground-500">
                    建议使用与真实身份无关的邮箱。不填写也可以，我们依然会阅读每一条线索。
                  </p>
                </div>

                <div className="news-tip-extra-field" aria-hidden="true">
                  <label htmlFor="tip-website-alt">请留空此栏</label>
                  <input
                    id="tip-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "error" && formError && (
                  <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}

                <div className="flex flex-col gap-4 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-shield-check-line text-base text-primary-600" aria-hidden="true"></i>
                    提交即表示你同意编辑部对内容进行脱敏与核实整理
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    <i
                      className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}
                      aria-hidden="true"
                    ></i>
                    {status === "submitting" ? "正在提交…" : "提交线索"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="reveal space-y-6 lg:sticky lg:top-40">
            <div className="rounded-lg border border-background-200 bg-secondary-900 p-6 text-background-100">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                <i className="ri-shield-user-line text-accent-400" aria-hidden="true"></i>
                保密说明
              </h2>
              <ul className="mt-4 space-y-3 text-xs leading-relaxed text-background-300">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-400" aria-hidden="true"></i>
                  投稿不收集手机号、身份证等可识别身份的信息
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-400" aria-hidden="true"></i>
                  线人代号在编辑部内部随机生成，不与真实身份关联
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-400" aria-hidden="true"></i>
                  发布前会与你确认哪些细节需要模糊处理
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-accent-500" aria-hidden="true"></i>
                投递小建议
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-time-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                  写清时间顺序：先发生的、后发生的，比结论更有用
                </li>
                <li className="flex gap-2">
                  <i className="ri-links-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                  用代号或职位描述代替真实姓名，同样能说清楚
                </li>
                <li className="flex gap-2">
                  <i className="ri-file-text-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                  手上如有单据或截图，先描述内容即可，不必上传原件
                </li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}